/**
 * PATH: packages/job-hub/src/jobs/client/filesReshape.ts
 * FILE_ID: JOBHUB-JOBS-CLIENT-FILES-001
 * PURPOSE: Pure reshapers: flat Django jobfile rows -> hub JobFileRecord /
 *          JobFilesPayload tree shape (Wave 2 union envelope + legacy array).
 * WHY: Moved VERBATIM from djangoJobHubClient.ts (S4 rank 12). Legacy JobFile
 *      ids are ints; QDrive FileRecord ids are UUID strings — Number() would
 *      turn those into NaN keys (that bug is paid for; don't re-author).
 */
import type { JobFileRecord } from '@quotech/tenant-shared/lib/jobFileTree'
import type { JobFilesPayload } from '../jobDetailTypes'

/** Flat Django jobfile -> hub JobFileRecord. Pure I->O. */
export function mapDjangoFile(f: Record<string, any>): JobFileRecord {
  // Legacy JobFile ids are ints; QDrive FileRecord ids are UUID strings
  // (Wave 2 union rail) — Number() would turn those into NaN keys.
  const rawId = String(f.id ?? '')
  return {
    id: /^\d+$/.test(rawId) ? Number(rawId) : rawId,
    file_name: f.filename ?? f.file_name ?? '',
    original_name: f.filename ?? f.original_name ?? '',
    folder_path: f.folder_path ?? '',
    category: f.category ?? '',
    mime_type: f.content_type ?? f.mime_type ?? null,
    file_size: f.file_size ?? null,
    source_type: f.source_type ?? 'upload',
    created_by: f.created_by ?? null,
    ai_summary: f.ai_summary ?? null,
    ai_provider: f.ai_provider ?? null,
    ai_confidence: f.ai_confidence ?? null,
    download_url: f.download_url ?? null,
    created_at: f.created_at ?? '',
    job_id: f.job ?? f.job_id ?? null,
    asset_id: f.asset_id ?? null,
    collapsed_ids: Array.isArray(f.collapsed_ids) ? f.collapsed_ids.map(String) : undefined,
  }
}

/** Django jobfiles union payload -> hub JobFilesPayload tree shape. Pure I->O.
 *  Accepts both the Wave 2 envelope ``{results, folder_paths}`` (from
 *  ?include=qdrive) and the legacy flat array, so a stale backend degrades
 *  to the old flat-file behaviour instead of breaking the rail. */
export function reshapeFilesPayload(jobId: number, data: unknown): JobFilesPayload {
  const rows: Record<string, any>[] = Array.isArray(data)
    ? data
    : ((data as any)?.results ?? [])
  const files = rows.map(mapDjangoFile)
  // Server-persisted folders (VirtualFolder-backed) come back on the
  // envelope; also derive paths the files themselves carry so records in
  // never-explicitly-created folders still group.
  const serverPaths: string[] = Array.isArray((data as any)?.folder_paths)
    ? (data as any).folder_paths
    : []
  const folder_paths = Array.from(
    new Set([
      ...serverPaths,
      ...files.map((f) => f.folder_path).filter((p): p is string => !!p),
    ]),
  )
  return { job_id: jobId, root_path: '', folder_paths, files }
}
